import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import { download, type Overview, type School } from './schoolApi';

const PERIODS = [7, 30, 90];
const QUIET_DAYS = 14;

function when(ms: number | null, t: ReturnType<typeof useT>): string {
  if (ms === null) return t('Not yet');
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function OverviewTab({ school }: { school: School }) {
  const t = useT();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    api<Overview>(`/school/overview?days=${days}`, { token: true })
      .then((o) => { if (live) setData(o); })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : t('Could not load the overview.')); });
    return () => { live = false; };
  }, [days, t]);

  async function csv() {
    setError(null);
    try {
      await download(`/school/overview.csv?days=${days}`, `pupil-progress-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be downloaded.'));
    }
  }

  const quietSince = Date.now() - QUIET_DAYS * 86_400_000;

  return (
    <div className="spaced">
      <div className="card">
        <div className="row" style={{ alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="school-period">{t('Period')}</label>
            <select id="school-period" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {PERIODS.map((d) => <option key={d} value={d}>{t('Last {n} days', { n: d })}</option>)}
            </select>
          </div>
          <button type="button" className="btn secondary" onClick={() => void csv()}>{t('Download as a spreadsheet (CSV)')}</button>
        </div>
        {school.role === 'TEACHER' && <p className="field-hint">{t('You see the pupils in the classes you teach.')}</p>}
        {error && <div className="error" role="alert">{error}</div>}
      </div>

      {data && (
        <>
          <div className="school-stats">
            <div className="card school-stat"><span className="school-stat-value">{data.pupils}</span><span>{t('Pupils')}</span></div>
            <div className="card school-stat"><span className="school-stat-value">{data.activePupils}</span><span>{t('Practised in this period')}</span></div>
            <div className="card school-stat"><span className="school-stat-value">{data.answered}</span><span>{t('Questions answered')}</span></div>
            <div className="card school-stat"><span className="school-stat-value">{data.correctPct === null ? '–' : `${data.correctPct}%`}</span><span>{t('Right first time')}</span></div>
          </div>

          <div className="card">
            <h3>{t('Classes')}</h3>
            {data.classes.length === 0 ? (
              <p className="muted">{school.role === 'ADMIN' ? t('No classes yet. Add them under Classes.') : t('You are not teaching a class yet. A school admin can add you to one.')}</p>
            ) : (
              <div className="table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('Class')}</th>
                      <th scope="col" className="num">{t('Pupils')}</th>
                      <th scope="col" className="num">{t('Practised')}</th>
                      <th scope="col" className="num">{t('Questions answered')}</th>
                      <th scope="col" className="num">{t('Right first time')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.classes.map((c) => (
                      <tr key={c.id}>
                        <th scope="row">{c.name}</th>
                        <td className="num">{c.pupils}</td>
                        <td className="num">{c.active}</td>
                        <td className="num">{c.answered}</td>
                        <td className="num">{c.correctPct === null ? '–' : `${c.correctPct}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <h3>{t('Pupils')}</h3>
            {data.pupilRows.length === 0 ? (
              <p className="muted">{t('No pupils to show.')}</p>
            ) : (
              <div className="table-wrap">
                <table className="report-table school-pupils">
                  <thead>
                    <tr>
                      <th scope="col">{t('Pupil')}</th>
                      <th scope="col">{t('Classes')}</th>
                      <th scope="col" className="num">{t('Questions answered')}</th>
                      <th scope="col" className="num">{t('Right first time')}</th>
                      <th scope="col" className="num">{t('Days practised')}</th>
                      <th scope="col">{t('Last practised')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pupilRows.map((p) => {
                      const quiet = p.lastPractisedAt === null || p.lastPractisedAt < quietSince;
                      return (
                        <tr key={p.id}>
                          <th scope="row">{p.name}</th>
                          <td>{p.classes.join(', ') || '–'}</td>
                          <td className="num">{p.answered}</td>
                          <td className="num">{p.correctPct === null ? '–' : `${p.correctPct}%`}</td>
                          <td className="num">{p.daysPractised}</td>
                          <td>
                            {when(p.lastPractisedAt, t)}
                            {quiet && <span className="school-flag">{t('Not in the last 2 weeks')}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
